import { fragment, Fragment, ObjectConfig, ObjectState, required } from "@homebound/form-state";
import {
  InputMaybe,
  ReadyPlanOptionGroupPersonalizationType,
  SavePersonalizationOptionGroupInput,
  SavePersonalizationPackageOptionInput,
  SavePersonalizationProductOptionInput,
} from "src/generated/graphqlTypes";
import {
  EditPersonalizationOptionWizard_MaterialVariantFragment,
  EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment,
  EditPersonalizationOptionWizard_Slot_TakeoffLineItemFragment,
  EditPersonalizationOptionWizardQuery,
} from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import { assertNever, fail } from "src/utils";

export type PackageOption = Pick<
  SavePersonalizationPackageOptionInput,
  "id" | "name" | "personalizationTargetCombinations"
> & {
  products: Array<{
    placeholderMaterialId: string;
    locationId?: string;
    productMaterial: { id: string; data: EditPersonalizationOptionWizard_MaterialVariantFragment } | undefined;
  }>;
};

export type ProductOption = Pick<
  SavePersonalizationProductOptionInput,
  "materialVariantId" | "personalizationTargetCombinations"
> & { data: Fragment<EditPersonalizationOptionWizard_MaterialVariantFragment> };

export type FormValue = Omit<
  SavePersonalizationOptionGroupInput,
  "packageOptions" | "productOptions" | "controlledSlots"
> & {
  placeholders: Array<{
    id: string;
    data: Fragment<EditPersonalizationOptionWizard_Slot_TakeoffLineItemFragment>;
    order?: number | null;
  }>;
  productOptions: Array<ProductOption>;
  packageOptions: Array<PackageOption>;
};

export const formConfig: ObjectConfig<FormValue> = {
  id: { type: "value" },
  type: { type: "value", rules: [required] },
  name: { type: "value", rules: [required] },
  marketingDescription: { type: "value" },
  levels: { type: "value" },
  displayPackageOptionsAsSingleProduct: { type: "value" },
  // Ideally this could be a `type: "fragment"` that was a `TliFragment[]` but
  // currently fragmentField too naively snapshots its value during the 1st init.map
  // run (when we have no data/are loading) and never updates it to the new value.
  placeholders: {
    type: "list",
    config: { id: { type: "value" }, data: { type: "fragment" }, order: { type: "value" } },
  },
  personalizationTargetGroupIds: { type: "value" },
  productOptions: {
    type: "list",
    config: {
      materialVariantId: { type: "value" },
      personalizationTargetCombinations: { type: "list", config: { options: { type: "value" } } },
      data: { type: "fragment" },
    },
  },
  packageOptions: {
    type: "list",
    config: {
      id: { type: "value" },
      name: { type: "value", rules: [required] },
      products: {
        type: "list",
        config: {
          placeholderMaterialId: { type: "value" },
          locationId: { type: "value" },
          productMaterial: { type: "value" },
        },
      },
      personalizationTargetCombinations: { type: "list", config: { options: { type: "value" } } },
    },
  },
  groupByFeature: { type: "value" },
};

export function mapToForm(data: EditPersonalizationOptionWizardQuery, isAdd: boolean, forTli?: string) {
  if (isAdd) {
    return {
      type: ReadyPlanOptionGroupPersonalizationType.SingleProduct,
      name: "",
      marketingDescription: "",
      levels: undefined,
      // When adding, the caller should pass in the initial placeholder via `forTli`, which we seed
      placeholders: [{ id: forTli ?? fail(), data: fragment(data.takeoffLineItem ?? fail()) }],
      // Product/Package options will be added by `setMaterialVariants` after the user selects the type
      productOptions: [],
      packageOptions: [],
      personalizationTargetGroupIds: data.takeoffLineItem?.targetGroups.map((tg) => tg.id),
    };
  }
  const rpog = data.readyPlan?.optionGroups.first ?? fail("No ReadyPlanOptionGroup found");
  const {
    id,
    name,
    marketingDescription,
    personalizationType,
    personalizationPlaceholders,
    personalizationTargetGroups,
    options,
    levels,
    displayPackageOptionsAsSingleProduct,
    groupByFeature,
  } = rpog;
  const type = personalizationType?.code;
  return {
    id,
    type,
    name,
    marketingDescription,
    personalizationTargetGroupIds: personalizationTargetGroups.map((tg) => tg.id),
    levels,
    displayPackageOptionsAsSingleProduct,
    groupByFeature,
    // When editing we show our controlled slots
    placeholders:
      personalizationPlaceholders
        ?.flatMap((pp) => pp.personalizationPlaceholderControlledSlots)
        .sortByKey("name")
        .map((tli) => {
          return { id: tli.id, data: fragment(tli), order: tli.personalizationSlotOrder };
        }) ?? [],
    // Form Package types we need to initialize the package options
    ...(type === ReadyPlanOptionGroupPersonalizationType.Package
      ? {
          productOptions: [],
          packageOptions: options.map((o) => ({
            id: o.id,
            name: o.name,
            // We scan look at our controlled slots, and their RPO placeholder's product cards, to find
            // find our products, i.e. product cards with "options=[Samsung Appliances]"
            products: personalizationPlaceholders.flatMap(
              (pp) =>
                pp.personalizationRpoShadowPlaceholder?.products
                  .filter((p) => p.options.every((opt) => opt.id === o.id))
                  .compact()
                  .map((p) => ({
                    placeholderMaterialId: p.placeholder!.materialVariant!.id,
                    productMaterial: p.materialVariant
                      ? { id: p.materialVariant.id, data: p.materialVariant }
                      : undefined,
                    locationId: groupByFeature ? p.location.id : undefined,
                  })) ?? [],
            ),
            personalizationTargetCombinations: rpoCombosToRpoIds(o.personalizationTargetCombinations),
          })),
        }
      : // Form Single/Linked types we need to initialize the materials
        {
          productOptions:
            options.map((rpo) => {
              return {
                materialVariantId: rpo.materialVariant!.id,
                data: fragment(rpo.materialVariant!),
                personalizationTargetCombinations: rpoCombosToRpoIds(rpo.personalizationTargetCombinations),
              };
            }) ?? [],
          packageOptions: [],
        }),
  };
}

export function rpoCombosToRpoIds(combos: { options: { id: string }[] }[]): { options: string[] }[] {
  return combos.map((combo) => ({ options: combo.options.map((o) => o.id) }));
}

/** Removes duplicate combinations from other formState rows based on the current row's selected values */
export function removeDuplicateCombinations<T extends ObjectState<ProductOption> | ObjectState<PackageOption>>(
  currentRow: T,
  allRows: readonly T[],
  selectedValues: string[],
) {
  allRows.forEach((row) => {
    if (row === currentRow) return;
    const current = row.personalizationTargetCombinations.value;
    const filtered = current.filter((c) => !selectedValues.includes(c.options.join(",")));
    if (filtered.length !== current.length) {
      row.personalizationTargetCombinations.set(filtered);
    }
  });
}

/**
 * Builds a map from RPO ID to its parent RPOG ID, to be used for partial combination matching. */
export function buildRpoIdToRpogIdMap(
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[],
): Map<string, string> {
  const map = new Map<string, string>();
  targetGroups.forEach((rpog) => rpog.options.forEach((rpo) => map.set(rpo.id, rpog.id)));
  return map;
}

/**
 * Checks if any of the product's combinations matches all the package option's combinations,
 * supporting partial combinations where omitted option groups = applies to all values.
 *
 * @param productCombinations - Product's target combinations (the pool to search within)
 * @param packageOptionCombinations - Package option's combinations (all must be matched)
 * @param rpoIdToRpogId - Map from RPO ID to its RPOG ID
 * @returns true if all package option combinations have a matching product combination
 */
function hasMatchingPartialCombination(
  productCombinations: { options: string[] }[],
  packageOptionCombinations: { options: string[] }[],
  rpoIdToRpogId: Map<string, string>,
): boolean {
  // All package option combinations must be matched by at least one product combination
  return (
    packageOptionCombinations.nonEmpty &&
    packageOptionCombinations.every((packageOptionCombo) => {
      const pkgRpogIds = packageOptionCombo.options.map((rpoId) => rpoIdToRpogId.get(rpoId)!).unique();
      return productCombinations.some((productCombo) => {
        // If product combo is more specific than package combo, don't match
        // e.g. package [Premium] should not match product [Bold, Premium]
        if (productCombo.options.length > packageOptionCombo.options.length) return false;
        return pkgRpogIds.every((rpogId) => {
          // We're at the RPOG level (e.g. SpecLevel), see what RPO (essential/deluxe/premium) each package/product combo is using
          const pkgRpoForRpog = packageOptionCombo.options.find((rpoId) => rpoIdToRpogId.get(rpoId) === rpogId);
          const prodRpoForRpog = productCombo.options.find((rpoId) => rpoIdToRpogId.get(rpoId) === rpogId);
          // If product combo doesn't have an RPO for this RPOG, we can assume it applies to all, so it's a match
          // Otherwise, check if the RPOs from the product and package combos match
          return !prodRpoForRpog || prodRpoForRpog === pkgRpoForRpog;
        });
      });
    })
  );
}

/** Initialize material variants for a new personalization option */
export function initializeOptions(
  personalizationType: ReadyPlanOptionGroupPersonalizationType,
  formState: ObjectState<FormValue>,
  tli: EditPersonalizationOptionWizard_Slot_TakeoffLineItemFragment,
) {
  // Group products by material variant to handle duplicated cards
  const groupedProducts = tli.products
    .filter((p) => !!p.materialVariant && p.active)
    .groupBy((product) => product.materialVariant!.id);
  // Update product/package options based on the personalization type
  if (
    personalizationType === ReadyPlanOptionGroupPersonalizationType.SingleProduct ||
    personalizationType === ReadyPlanOptionGroupPersonalizationType.LinkedProduct
  ) {
    // We've already initialized, and the useEffect ran again
    if (formState.productOptions.value?.length > 0) return;
    // productOptions only need `materialVariantId` & `combos` set...
    formState.productOptions.set(
      groupedProducts.toEntries().map(([materialVariantId, products]) => ({
        materialVariantId,
        data: fragment(products.first!.materialVariant!),
        // Use all target combinations for the material variant
        personalizationTargetCombinations: rpoCombosToRpoIds(products.flatMap((p) => p.targetCombinations)),
      })),
    );
  } else if (personalizationType === ReadyPlanOptionGroupPersonalizationType.Package) {
    // We've already initialized, and the useEffect ran again
    if (formState.packageOptions.value?.length > 0) return;
    // packageOptions need the "column" `placeholderMaterialId` + cells of `productMaterial`s
    // Because this is the 1st placeholder, we create 1 row for each existing card (i.e. two refrigerators become
    // a GE Appliance row & Samsung Appliance row, although the user must still add the option names)
    formState.packageOptions.set(
      groupedProducts.toEntries().map(([_, products]) => ({
        products: [
          {
            placeholderMaterialId: tli.materialVariant!.id,
            productMaterial: { id: products.first!.materialVariant!.id, data: products.first!.materialVariant! },
            // This is a good default for groupByFeature; but if groupByFeature is disabled, it ends up being fine anyway
            // b/c a) the FE code will flip to "unique by placeholderMaterialId", and b) the BE will ignore our locationId
            locationId: tli.location.id,
          },
        ],
        personalizationTargetCombinations: rpoCombosToRpoIds(products.flatMap((p) => p.targetCombinations)),
      })),
    );
  } else {
    assertNever(personalizationType);
  }
}

/** Initialize material variants when adding a new slot to a personalization option */
export function addExistingMaterials(
  formState: ObjectState<FormValue>,
  placeholder: EditPersonalizationOptionWizard_Slot_TakeoffLineItemFragment,
  rpoIdToRpogId: Map<string, string>,
) {
  const personalizationType = formState.type.value!;
  if (
    personalizationType === ReadyPlanOptionGroupPersonalizationType.SingleProduct ||
    personalizationType === ReadyPlanOptionGroupPersonalizationType.LinkedProduct
  ) {
    // For Single/Linked options, we should keep new product cards and add them to the material variant pool
    placeholder.products
      .filter((tli) => !!tli.materialVariant && tli.active)
      .filter((tli) => !formState.productOptions.value.some((po) => po.materialVariantId === tli.materialVariant!.id))
      .forEach((tli) => {
        formState.productOptions.add({
          materialVariantId: tli.materialVariant!.id,
          data: fragment(tli.materialVariant!),
          personalizationTargetCombinations: [],
        });
      });
  } else if (personalizationType === ReadyPlanOptionGroupPersonalizationType.Package) {
    // For package options, set material variants for the new placeholder when target combinations match specific option sets
    // Group products by material variant to handle duplicated cards
    placeholder.products
      .filter((p) => !!p.materialVariant && p.active)
      .groupBy((product) => product.materialVariant!.id)
      .toEntries()
      .forEach(([materialVariantId, products]) => {
        // Merge all target combinations from products sharing the same material variant
        const targetCombinations = rpoCombosToRpoIds(products.flatMap((p) => p.targetCombinations));
        // Get all matching package options using partial combination matching
        const matchingPackageOptions = formState.packageOptions.rows.filter((po) =>
          hasMatchingPartialCombination(targetCombinations, po.personalizationTargetCombinations.value, rpoIdToRpogId),
        );
        // Add material variants to each matching package option
        matchingPackageOptions.forEach((matchingPackageOption) => {
          // If the package option already has a product assigned to this cell, don't add a 2nd/duplicate product
          if (
            !matchingPackageOption.products.value.some(
              (p) =>
                p.placeholderMaterialId === placeholder.materialVariant!.id &&
                (!formState.groupByFeature.value || p.locationId === placeholder.location.id),
            )
          ) {
            matchingPackageOption.products.add({
              placeholderMaterialId: placeholder.materialVariant!.id,
              productMaterial: { id: materialVariantId, data: products.first!.materialVariant! },
              locationId: placeholder.location.id,
            });
          }
        });
      });
  } else {
    assertNever(personalizationType);
  }
}

export function maybeFilterByPlaceholder(form: ObjectState<FormValue>) {
  const type = form.type.value;
  if (!type) return;
  if (
    type === ReadyPlanOptionGroupPersonalizationType.LinkedProduct ||
    type === ReadyPlanOptionGroupPersonalizationType.SingleProduct
  ) {
    return form.placeholders.rows.first?.data.value.materialVariant?.id;
  } else if (type === ReadyPlanOptionGroupPersonalizationType.Package) {
    return undefined;
  } else {
    assertNever(type);
  }
}

/** Toggles package options between groupByFeature on/off. */
export function maybeGroupByFeature(
  formState: ObjectState<FormValue>,
  groupByFeature: boolean,
  rpoIdToRpogId: Map<string, string>,
) {
  // If group by feature is enabled, there should be a product for every location + material variant in all package options
  if (groupByFeature) {
    // Set appropriate locations for existing products (i.e., use the first controlled placeholder's location per material variant)
    const locationIdByPlaceholderMvId = formState.placeholders.rows
      .uniqueBy((p) => p.data.value.materialVariant!.id)
      .keyBy(
        (p) => p.data.value.materialVariant!.id,
        (p) => p.data.value.location.id,
      );
    formState.packageOptions.rows
      .flatMap((po) => po.products.rows)
      .forEach((p) => p.locationId.set(locationIdByPlaceholderMvId[p.placeholderMaterialId.value]));
    const assignedColumns = new Set(
      formState.packageOptions.value.flatMap((po) => po.products).map((p) => p.locationId + p.placeholderMaterialId),
    );
    const emptyPlaceholders = formState.placeholders.rows.filter((placeholder) => {
      const { location, materialVariant } = placeholder.data.value;
      return !assignedColumns.has(location.id + materialVariant?.id);
    });
    // Add material variants for missing placeholders
    emptyPlaceholders.forEach((placeholder) => addExistingMaterials(formState, placeholder.data.value, rpoIdToRpogId));
  } else {
    // If not enabled, ensure there's just one product for every material variant
    formState.packageOptions.rows.forEach((po) => {
      // We cannot use our placeholder.locations (i.e. `Bath / Shower` or `Bath`, to use that for "picking a winner",
      // which is what we originally tried here) because when `groupByFeature` is off, the backend will nudge our
      // productTlis to match the `RPOG.location`, which for a cross-room option might be `Building`.
      //
      // So instead just do a `groupBy(placeholder)` and pick a random/first winner.
      po.products.value
        .groupByObject((po) => po.placeholderMaterialId)
        .filter(([, matches]) => matches.length > 1)
        .forEach(([, matches]) => {
          const [, ...losers] = matches;
          losers.forEach((loser) => po.products.remove(loser));
        });
      // Ensure all product locations are unset because they're not used when group by feature is off.
      // We want to make sure we don't unnecessarily introduce changed values when toggling group by feature on and off
      // (i.e., the form state is initialized with `locationId: undefined` when group by feature is off).
      po.products.rows.forEach((p) => p.locationId.set(undefined));
    });
  }
}

/** Determines whether target group/combinations can be assigned given the personalization type */
export function canAssignTargetCombinations(
  personalizationType: InputMaybe<ReadyPlanOptionGroupPersonalizationType>,
): boolean {
  // Hide target groups section for single product options, since target combinations should be defined
  // at the granular level of the placeholder cards, differently from Linked Product and Package options that default combos accross all slots.
  return personalizationType !== ReadyPlanOptionGroupPersonalizationType.SingleProduct;
}

/** Move the edited slot to its (clamped) position, shifting others, then renumber 1..n. */
export function reorderSlots(
  rows: Array<{ id: string; order: number | null | undefined }>,
  editedId: string,
): Array<{ id: string; order: number | null }> {
  // Keep only slots that have an order, narrowed to `number` by compact().
  const ordered = rows.map((r) => (r.order != null ? { id: r.id, order: r.order } : undefined)).compact();
  const unordered = rows.filter((r) => r.order == null);

  const editedRow = ordered.find((r) => r.id === editedId);
  if (!editedRow) return rows.map((r) => ({ id: r.id, order: r.order ?? null }));

  // Drop the edited slot, sort the rest by order, then reinsert at the clamped position.
  const others = ordered.filter((r) => r.id !== editedId).sortBy((r) => r.order);
  const targetPos = Math.min(Math.max(1, editedRow.order), ordered.length);
  others.splice(targetPos - 1, 0, editedRow);

  return [...others.map((r, i) => ({ id: r.id, order: i + 1 })), ...unordered.map((r) => ({ id: r.id, order: null }))];
}

export function applySlotReorder(formState: ObjectState<FormValue>, editedId: string) {
  const row = formState.placeholders.rows.find((r) => r.id.value === editedId);
  if (row?.order.value == null) return;
  const updatedOrders = reorderSlots(
    formState.placeholders.rows.map((r) => ({ id: r.id.value, order: r.order.value })),
    editedId,
  );
  const orderById = new Map(updatedOrders.map((u) => [u.id, u.order]));
  // Batch the renumbering into a single list mutation rather than N per-row sets.
  formState.placeholders.set(
    formState.placeholders.value.map((p) => ({ ...p, order: orderById.has(p.id) ? orderById.get(p.id)! : p.order })),
  );
}

/** Adds new placeholders with auto-assigned order, sorted by personalizationSlotOrder. */
export function addPlaceholdersWithOrder(
  formState: ObjectState<FormValue>,
  placeholders: EditPersonalizationOptionWizard_Slot_TakeoffLineItemFragment[],
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[],
) {
  const rpoIdToRpogId = buildRpoIdToRpogIdMap(targetGroups);
  const existingOrders = formState.placeholders.rows.map((r) => r.order.value).compact();
  const currentMaxOrder = existingOrders.max() ?? 0;
  const sorted = [...placeholders].sortBy((p) => p.personalizationSlotOrder ?? 0);
  sorted.forEach((p, i) => {
    formState.placeholders.add({ id: p.id, data: fragment(p), order: currentMaxOrder + i + 1 });
    addExistingMaterials(formState, p, rpoIdToRpogId);
  });
}
